import React, { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { defaultSettings, defaultItems, periodOf } from "../utils/payroll";

// 賃金台帳のダウンロード。
// 労働基準法施行規則第54条が求める記載事項（氏名・性別・賃金計算期間・労働日数・
// 労働時間数・時間外／深夜／休日の労働時間数・賃金の種類ごとの額・控除の額）を
// 確定済みの給与から出力する。法定帳簿は3年間の保存義務があるため、
// 年度単位でまとめて出せるようにしてある。

const thisYear = () => new Date().getFullYear();

function WageLedger({ companyId, onBack }) {
  const [year, setYear]       = useState(thisYear);
  const [employees, setEmployees] = useState([]);
  const [profiles, setProfiles]   = useState({});
  const [payrolls, setPayrolls]   = useState([]);
  const [settings, setSettings]   = useState(defaultSettings(companyId));
  const [checked, setChecked]     = useState({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [includeDraft, setIncludeDraft] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const sSnap = await getDoc(doc(db, "payrollSettings", companyId));
      setSettings(sSnap.exists() ? { ...defaultSettings(companyId), ...sSnap.data() } : defaultSettings(companyId));

      const uSnap = await getDocs(query(
        collection(db, "users"),
        where("companyId", "==", companyId),
        where("status", "==", "approved")
      ));
      setEmployees(uSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.nameKana || a.name || "").localeCompare(b.nameKana || b.name || "", "ja")));

      const pSnap = await getDocs(query(collection(db, "payrollProfiles"), where("companyId", "==", companyId)));
      const pm = {};
      pSnap.docs.forEach(d => { pm[d.id] = d.data(); });
      setProfiles(pm);

      const rSnap = await getDocs(query(collection(db, "payrolls"), where("companyId", "==", companyId)));
      setPayrolls(rSnap.docs.map(d => d.data()));
    } catch (e) {
      console.error("賃金台帳のデータ取得に失敗しました:", e);
      setError("データを読み込めませんでした。権限をご確認ください。");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const targets = () => {
    const ids = employees.filter(e => checked[e.id]).map(e => e.id);
    return ids.length > 0 ? ids : employees.map(e => e.id);
  };

  const rowsForYear = () => payrolls
    .filter(r => String(r.yearMonth || "").startsWith(String(year)))
    .filter(r => includeDraft || r.status === "confirmed");

  const download = () => {
    setError("");
    const ids = targets();
    const rows = rowsForYear().filter(r => ids.includes(r.uid));
    if (rows.length === 0) {
      setError(`${year}年の${includeDraft ? "" : "確定済みの"}給与データがありません。`);
      return;
    }

    // 支給・控除の項目名は、その月に保存された定義を使う。
    // 年の途中で項目名を変えても、台帳の列がずれないよう全期間の項目を集める。
    const earnCols = [], dedCols = [];
    rows.forEach(r => {
      (r.items || defaultItems()).forEach(it => {
        const arr = it.type === "控除" ? dedCols : earnCols;
        if (!arr.some(x => x.key === it.key)) arr.push({ key: it.key, name: it.name });
      });
    });

    const head = [
      "社員ID", "氏名", "性別", "雇用形態", "対象年月", "賃金計算期間",
      "労働日数", "労働時間数", "時間外労働時間数", "深夜労働時間数", "休日労働時間数",
      ...earnCols.map(c => `支給:${c.name}`), "支給合計",
      ...dedCols.map(c => `控除:${c.name}`), "控除合計",
      "差引支給額", "状態",
    ];

    const body = rows
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ja") || a.yearMonth.localeCompare(b.yearMonth))
      .map(r => {
        const prof = profiles[r.uid] || {};
        const emp  = employees.find(e => e.id === r.uid) || {};
        const { from, to } = periodOf(r.yearMonth, settings);
        const att = r.attendance || {};
        const workedHours = (Number(att.plainHours || 0) + Number(att.withinOtHours || 0)
          + Number(att.overtimeHours || 0) + Number(att.holidayHours || 0)).toFixed(2);

        return [
          r.memberNo || emp.memberNo || "",
          r.name || emp.name || "",
          prof.gender || "",
          prof.employmentType || "",
          r.yearMonth,
          `${from.toLocaleDateString("ja-JP")}〜${to.toLocaleDateString("ja-JP")}`,
          att.workDays ?? "",
          workedHours,
          (Number(att.withinOtHours || 0) + Number(att.overtimeHours || 0)).toFixed(2),
          att.nightHours ?? "",
          att.holidayHours ?? "",
          ...earnCols.map(c => Number(r.amounts?.[c.key] || 0)),
          r.totalEarnings ?? "",
          ...dedCols.map(c => Number(r.amounts?.[c.key] || 0)),
          r.totalDeductions ?? "",
          r.netPay ?? "",
          r.status === "confirmed" ? "確定済み" : "下書き",
        ];
      });

    const BOM = "﻿";
    const csv = BOM + [head, ...body]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `賃金台帳_${year}年${ids.length === 1 ? `_${rows[0].name || ""}` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const years = [...new Set(payrolls.map(r => String(r.yearMonth || "").slice(0, 4)).filter(Boolean))];
  const available = rowsForYear();
  const allChecked = employees.length > 0 && employees.every(e => checked[e.id]);
  const noGender = employees.filter(e => !(profiles[e.id]?.gender));

  return (
    <div style={st.container}>
      <button style={st.back} onClick={onBack}>← 給与計算に戻る</button>
      <h2 style={st.title}>📒 賃金台帳</h2>
      <p style={st.lead}>
        労働基準法で作成と3年間の保存が義務づけられている帳簿です。確定した給与から、
        氏名・性別・賃金計算期間・労働日数・労働時間数・時間外／深夜／休日の労働時間数・
        支給項目ごとの額・控除額を出力します。
      </p>

      <div style={st.toolbar}>
        <div>
          <label style={st.label}>対象年</label>
          <select style={st.select} value={year} onChange={e => setYear(Number(e.target.value))}>
            {[...new Set([...years, String(thisYear())])].sort().map(y => (
              <option key={y} value={y}>{y}年</option>
            ))}
          </select>
        </div>
        <div style={st.toolRight}>
          <label style={st.check}>
            <input type="checkbox" checked={includeDraft} onChange={e => setIncludeDraft(e.target.checked)} style={st.cb} />
            下書きも含める
          </label>
          <button style={st.dlBtn} onClick={download} disabled={loading}>CSVをダウンロード</button>
        </div>
      </div>

      {error && <p style={st.error}>{error}</p>}

      {noGender.length > 0 && (
        <div style={st.warn}>
          ⚠️ 性別が未登録の社員が{noGender.length}名います（{noGender.slice(0, 5).map(e => e.name || e.email).join("、")}
          {noGender.length > 5 ? " ほか" : ""}）。
          性別は賃金台帳の法定記載事項です。社員管理の「給与情報」からご登録ください。
        </div>
      )}

      <p style={st.count}>
        {loading ? "読み込み中..." : `${year}年の対象データ：${available.length}件`}
        {!includeDraft && "（確定済みのみ）"}
      </p>

      {employees.length > 0 && (
        <div style={st.pickBox}>
          <div style={st.pickHead}>
            <label style={st.check}>
              <input type="checkbox" checked={allChecked} style={st.cb}
                onChange={() => {
                  if (allChecked) setChecked({});
                  else { const n = {}; employees.forEach(e => { n[e.id] = true; }); setChecked(n); }
                }} />
              全員を選択
            </label>
            <span style={st.pickNote}>未選択のときは全員が対象になります</span>
          </div>
          <div style={st.pickList}>
            {employees.map(e => (
              <label key={e.id} style={st.pickItem}>
                <input type="checkbox" style={st.cb} checked={!!checked[e.id]}
                  onChange={ev => setChecked({ ...checked, [e.id]: ev.target.checked })} />
                {e.name || e.email}
              </label>
            ))}
          </div>
        </div>
      )}

      <div style={st.help}>
        <strong>出力される内容</strong>
        <p style={st.helpBody}>
          1行が「社員1人の1か月分」です。年間分をまとめて出すので、Excelで社員ごとに絞り込んで印刷・保存できます。
          支給項目・控除項目の列は、その月に保存された項目定義から作ります。年の途中で項目名を変更しても、
          過去の記録は当時の名称のまま残ります。
        </p>
      </div>
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "820px", margin: "0 auto" },
  back: { background: "none", border: "none", color: "#2980b9", cursor: "pointer", fontSize: "14px", padding: 0, marginBottom: "12px" },
  title: { color: "#2c3e50", marginBottom: "6px" },
  lead: { color: "#7f8c8d", fontSize: "14px", lineHeight: "1.8", marginTop: 0, marginBottom: "18px" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "16px", flexWrap: "wrap", backgroundColor: "#fff", padding: "16px 20px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "12px" },
  toolRight: { display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" },
  label: { display: "block", fontSize: "13px", color: "#2c3e50", fontWeight: "bold", marginBottom: "6px" },
  select: { padding: "9px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", backgroundColor: "#fff" },
  check: { display: "flex", alignItems: "center", fontSize: "14px", color: "#2c3e50", cursor: "pointer" },
  cb: { width: "16px", height: "16px", marginRight: "8px" },
  dlBtn: { padding: "10px 20px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  error: { color: "#e74c3c", fontSize: "14px", marginBottom: "10px" },
  warn: { backgroundColor: "#fff6e5", border: "1px solid #f5c26b", borderRadius: "10px", padding: "12px 16px", marginBottom: "12px", fontSize: "13px", color: "#8a5a00", lineHeight: "1.7" },
  count: { fontSize: "13px", color: "#5d6d7e", marginTop: 0, marginBottom: "12px" },
  pickBox: { border: "1px solid #e6ebf0", borderRadius: "10px", padding: "12px 14px", backgroundColor: "#fafbfc", marginBottom: "16px" },
  pickHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "8px" },
  pickNote: { fontSize: "12px", color: "#7f8c8d" },
  pickList: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "4px", maxHeight: "240px", overflowY: "auto" },
  pickItem: { display: "flex", alignItems: "center", fontSize: "14px", color: "#2c3e50", cursor: "pointer", padding: "4px 0" },
  help: { backgroundColor: "#eef2f6", borderRadius: "10px", padding: "14px 18px", fontSize: "13px", color: "#2c3e50" },
  helpBody: { color: "#5d6d7e", lineHeight: "1.8", marginTop: "6px", marginBottom: 0 },
};

export default WageLedger;
